import * as React from "react";
import * as HoverCardPrimitive from "@radix-ui/react-hover-card";
import { motion } from "framer-motion";

import { cn } from "@/lib/utils";

type HoverCardRootProps = React.ComponentPropsWithoutRef<
  typeof HoverCardPrimitive.Root
>;

type HoverCardCtxValue = {
  isTouch: boolean;
  open: boolean;
  setOpen: (open: boolean) => void;
  toggle: () => void;
};

const HoverCardCtx = React.createContext<HoverCardCtxValue | null>(null);

const getIsTouchDevice = () => {
  if (typeof window === "undefined") return false;
  return "ontouchstart" in window || (navigator?.maxTouchPoints ?? 0) > 0;
};

const HoverCard = ({
  openDelay = 200,
  closeDelay = 100,
  open: openProp,
  onOpenChange,
  children,
  ...rest
}: HoverCardRootProps) => {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(false);
  const [isTouchDevice] = React.useState(getIsTouchDevice);

  const isControlled = openProp !== undefined;
  const open = isControlled ? (openProp as boolean) : uncontrolledOpen;

  const setOpen = React.useCallback(
    (next: boolean) => {
      if (!isControlled) setUncontrolledOpen(next);
      onOpenChange?.(next);
    },
    [isControlled, onOpenChange],
  );

  const toggle = React.useCallback(() => {
    if (isControlled) {
      const next = !open;
      onOpenChange?.(next);
      return;
    }

    setUncontrolledOpen((prev) => {
      const next = !prev;
      onOpenChange?.(next);
      return next;
    });
  }, [isControlled, open, onOpenChange]);

  const ctx = React.useMemo<HoverCardCtxValue>(
    () => ({
      isTouch: isTouchDevice,
      open,
      setOpen,
      toggle,
    }),
    [isTouchDevice, open, setOpen, toggle],
  );

  // Mobile/touch devices render the trigger children statically without
  // hover-card interaction per product direction.
  if (isTouchDevice) {
    return <>{children}</>;
  }

  return (
    <HoverCardCtx.Provider value={ctx}>
      <HoverCardPrimitive.Root
        {...rest}
        openDelay={openDelay}
        closeDelay={closeDelay}
      >
        {children}
      </HoverCardPrimitive.Root>
    </HoverCardCtx.Provider>
  );
};

const HoverCardTrigger = React.forwardRef<
  React.ElementRef<typeof HoverCardPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof HoverCardPrimitive.Trigger> & { showTapHint?: boolean }
>(({ children, onClick, showTapHint = false, className, ...props }, ref) => {
  const ctx = React.useContext(HoverCardCtx);

  // On touch devices the hover card is disabled, so render the trigger
  // children as a plain static wrapper.
  if (!ctx) {
    return (
      <span ref={ref as React.Ref<HTMLSpanElement>} className={cn("relative inline-block", className)} {...props}>
        {children}
      </span>
    );
  }

  return (
    <HoverCardPrimitive.Trigger
      ref={ref}
      asChild
      {...props}
      onClick={(e) => {
        if (ctx?.isTouch) {
          e.preventDefault();
          ctx.toggle();
        }
        onClick?.(e);
      }}
    >
      <span className={cn("relative inline-block", className)}>
        {children}
        {ctx?.isTouch && showTapHint && !ctx.open && (
          <span className="absolute -bottom-5 left-1/2 -translate-x-1/2 whitespace-nowrap text-[10px] text-muted-foreground/70 pointer-events-none animate-fade-in">
            Tap for details
          </span>
        )}
      </span>
    </HoverCardPrimitive.Trigger>
  );
});
HoverCardTrigger.displayName = HoverCardPrimitive.Trigger.displayName;

const HoverCardContent = React.forwardRef<
  React.ElementRef<typeof HoverCardPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof HoverCardPrimitive.Content>
>(({ className, align = "center", sideOffset = 4, children, ...props }, ref) => {
  const ctx = React.useContext(HoverCardCtx);

  // Hover cards are disabled on touch devices, so no content is rendered.
  if (!ctx) return null;

  return (
    <HoverCardPrimitive.Portal>
      <HoverCardPrimitive.Content
        ref={ref}
        align={align}
        sideOffset={sideOffset}
        asChild
        {...props}
      >
        <motion.div
          initial={{ opacity: 0, y: 8, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
          className={cn(
            "z-50 w-64 rounded-md border bg-popover p-4 text-popover-foreground shadow-md outline-none",
            className,
          )}
        >
          {children}
        </motion.div>
      </HoverCardPrimitive.Content>
    </HoverCardPrimitive.Portal>
  );
});
HoverCardContent.displayName = HoverCardPrimitive.Content.displayName;

export { HoverCard, HoverCardTrigger, HoverCardContent };
